import React, { useState, useEffect, useRef } from "react";
import Fuse from "fuse.js";
import Select from "react-select";
import { FieldProps } from "formik";
import { ClipLoader } from "react-spinners";

import MenuList from "./MenuList";
import debounce from "debounce-promise";
import { getLastUsedBids, getBids } from "../../../../../../docuclipper/api";

const wait = 50;

export const QboSearchField: React.FC<FieldProps<any>> = ({
  field,
  form,
  meta,
}) => (
  <>
    <QboSearch field={field} form={form} meta={meta} />
    {(form.touched[field.name] || form.submitCount > 0) &&
      form.errors[field.name] && (
        <div className="text-danger">{String(form.errors[field.name])}</div>
      )}
  </>
);

export const QboSearch: React.FC<FieldProps<any>> = ({ field, form, meta }) => {
  const [data, setData] = useState<any[]>([]);
  const [fuse, setFuse] = useState<Fuse<any>>(new Fuse([], { keys: ["name"] }));
  const [recentBids, setRecentBids] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // Track load failures so the dropdown surfaces an error instead of staying
  // stuck on "Loading banks..." (rating 10998, job 2562516: user submitted
  // a custom-all CSV export instead of QBO; if getBids() failed silently the
  // Select would never render and the user has no way to know QBO was broken).
  const [loadError, setLoadError] = useState(false);
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    async function fetchData() {
      try {
        const bidsData = await getLastUsedBids();
        if (isMounted.current) {
          setRecentBids(
            bidsData.map((bid) => ({ bid: bid.bid, name: bid.bankName }))
          );
        }
      } catch (error) {
        console.error("Error fetching recent bids:", error);
      }
    }
    fetchData();
  }, []);

  useEffect(() => {
    async function loadQboData() {
      try {
        setIsLoading(true);
        setLoadError(false);
        const bidsData = await getBids();
        // console.log("Raw bids data:", bidsData);
        const newData = bidsData.map((d) => ({
          ...d,
          label: d.name,
          value: d.bid,
        }));
        // console.log("Processed new data:", newData);

        const newDataWithRecentBids = recentBids
          .map(({ bid, name }) => ({
            bid: bid as any,
            name,
            value: bid,
            label: name,
          }))
          .concat(newData);
        // console.log("Combined data with recent bids:", newDataWithRecentBids);

        if (isMounted.current) {
          setData(newDataWithRecentBids);
          setFuse(new Fuse(newDataWithRecentBids, { keys: ["name"] }));

          // Set default value for Chase Web Download
          const chaseOption = newDataWithRecentBids.find(
            (option) =>
              option.bid === "02430" && option.name === "Chase Web Download"
          );
          // console.log("Found Chase option:", chaseOption);
          // console.log("Current field value:", field.value);
          if (chaseOption) {
            // console.log("Setting Chase as default value");
            form.setFieldValue(field.name, chaseOption);
            // console.log("New field value:", form.values[field.name]);
          } else {
            // console.log("Chase option not found in data");
          }
        }
      } catch (error) {
        console.error("Error loading QBO data:", error);
        if (isMounted.current) {
          setLoadError(true);
        }
      } finally {
        if (isMounted.current) {
          setIsLoading(false);
        }
      }
    }
    loadQboData();
  }, [recentBids]);

  const searchOptions = (inputValue: string) =>
    new Promise((resolve) => {
      if (!inputValue) {
        resolve(data);
      } else {
        resolve(fuse.search(inputValue).map((c) => c.item));
      }
    });

  const debouncedLoadOptions = debounce(searchOptions, wait);

  return (
    <>
      {isLoading ? (
        <div className="d-flex align-items-center">
          <div className="mx-2">
            <ClipLoader size={20} color="#6c757d" />
          </div>
          <span>Loading banks...</span>
        </div>
      ) : loadError || data.length === 0 ? (
        <div className="text-danger">
          We couldn't load the bank list. Please refresh the page and try again,
          or contact support if the problem persists.
        </div>
      ) : (
        <Select
          placeholder="Search banks ..."
          name={field.name}
          components={{ MenuList }}
          options={data}
          isSearchable={true}
          defaultOptions={true}
          value={field.value}
          onChange={(option) => form.setFieldValue(field.name, option)}
          onBlur={field.onBlur}
          loadOptions={debouncedLoadOptions}
        />
      )}
    </>
  );
};

export default QboSearchField;
